import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Message } from './ChatInterface'
import { useSubmitFeedback } from '@/hooks/useFeedback'
import { FeedbackFilterConfig } from '@/api/feedbackService'
import { CitationSource } from '@/api/chatService'
import nimonikLogo from '../../assets/images/nimonik-logo.png'

interface MessageListProps {
  messages: Message[]
  conversationId: number | null
  filterConfig?: FeedbackFilterConfig | null
  onCitationClick?: (source: CitationSource, citedText: string, positionFraction: number | null) => void
}

export default function MessageList({ messages, conversationId, filterConfig, onCitationClick }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 space-y-8 max-w-4xl mx-auto w-full">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} conversationId={conversationId} filterConfig={filterConfig} onCitationClick={onCitationClick} />
      ))}

      <div ref={messagesEndRef} />
    </div>
  )
}

function MessageBubble({ message, conversationId, filterConfig, onCitationClick }: { message: Message; conversationId: number | null; filterConfig?: FeedbackFilterConfig | null; onCitationClick?: (source: CitationSource, citedText: string, positionFraction: number | null) => void }) {
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(message.feedbackType ?? null)
  const [feedbackText, setFeedbackText] = useState('')
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(message.feedbackType != null)
  const isUser = message.role === 'user'
  const feedbackMutation = useSubmitFeedback()
  const { t, i18n } = useTranslation()

  const messageId = parseInt(message.id, 10)
  const canSubmitFeedback = conversationId != null && !isNaN(messageId)

  const handleThumbsUp = () => {
    if (feedbackSubmitted) return
    setFeedbackType(feedbackType === 'positive' ? null : 'positive')
    setFeedbackText('')
  }

  const handleThumbsDown = () => {
    if (feedbackSubmitted) return
    setFeedbackType(feedbackType === 'negative' ? null : 'negative')
    setFeedbackText('')
  }

  const handleSubmitFeedback = () => {
    if (!feedbackType || !canSubmitFeedback) return

    feedbackMutation.mutate(
      {
        conversation_id: conversationId!,
        message_id: messageId,
        feedback_type: feedbackType,
        feedback_text: feedbackText.trim() || null,
        filter_config: filterConfig || undefined,
      },
      {
        onSuccess: () => {
          setFeedbackSubmitted(true)
          setFeedbackText('')
        },
        onError: (error) => {
          console.error('Failed to submit feedback:', error)
        },
      },
    )
  }

  const formatTime = (date: Date) => {
    const d = new Date(date)
    const now = new Date()
    const locale = i18n.language
    const isToday = d.toDateString() === now.toDateString()
    const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    if (isToday) return time
    const dateStr = d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    return `${dateStr}, ${time}`
  }

  if (isUser) {
    return (
      <div className="flex flex-col items-end">
        <div className="bg-brand text-white px-5 py-3 rounded-2xl rounded-tr-sm max-w-lg shadow-sm text-sm leading-relaxed">
          {message.content}
        </div>
        <span className="text-[10px] text-gray-400 mt-1 mr-1">{formatTime(message.timestamp)}</span>
      </div>
    )
  }

  return (
    <div className="flex justify-start">
      {/* Bot Avatar */}
      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center mr-3 flex-shrink-0 mt-1 overflow-hidden p-1">
        <img
          src={nimonikLogo}
          alt="Nimonik Logo"
          className="h-full object-contain"
        />
      </div>

      <div className="max-w-2xl flex-1">
        <div className="text-sm text-gray-800 leading-relaxed prose prose-sm max-w-none prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-strong:text-gray-900 prose-a:text-brand">
          {message.isStreaming && !message.content ? (
            <div className="flex items-center gap-2 pt-2">
              <Loader2 className="w-4 h-4 text-brand animate-spin" />
              <span className="text-sm text-gray-500">{t('chat.thinking')}</span>
            </div>
          ) : (
            <>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  a: ({ href, children, className, ...props }) => {
                    // nim-source-doc: doc title link in the sources footer
                    // If viewable, open the document panel at page 1 (no citation)
                    // instead of following the URL
                    if (className === 'nim-source-doc' && onCitationClick) {
                      const sources = message.sources
                      const dataProps = props as Record<string, unknown>
                      const legislationUuid = String(dataProps['data-legislation-uuid'] ?? '')
                      const source = sources?.find((s) =>
                        (legislationUuid && s.legislation_uuid === legislationUuid) ||
                        s.url === href ||
                        s.title === String(children)
                      )
                      if (source?.viewable) {
                        return (
                          <a
                            className={className}
                            onClick={(e) => { e.preventDefault(); onCitationClick(source, '', null) }}
                            title={source.title}
                            {...props}
                          >
                            {children}
                          </a>
                        )
                      }
                    }
                    return <a href={href} className={className} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
                  },
                  // Intercept nim-cite and nim-chip spans injected by the backend.
                  span: ({ className, children, ...props }) => {
                    const sources = message.sources
                    const citations = message.citations

                    // nim-cite: inline citation marker in the response text
                    if (className === 'nim-cite') {
                      const sourceIndex = parseInt(String(children), 10)
                      if (!isNaN(sourceIndex) && sources && onCitationClick) {
                        // source_index is now unique per citation — find exact match
                        const source = sources.find((s) => s.index === sourceIndex)
                        const citation = citations?.find((c) => c.source_index === sourceIndex)
                        // Show tooltip even if not viewable
                        const tooltip = [source?.title, citation?.cited_text].filter(Boolean).join('\n\n')
                        if (source?.viewable) {
                          return (
                            <span
                              className="nim-cite"
                              onClick={() => onCitationClick(source, citation?.cited_text ?? '', citation?.position_fraction ?? null)}
                              title={tooltip}
                            >
                              {children}
                            </span>
                          )
                        }
                        // Not viewable but still show tooltip
                        if (tooltip) {
                          return <span className="nim-cite" style={{ cursor: 'default' }} title={tooltip}>{children}</span>
                        }
                      }
                      return <span className="nim-cite" style={{ cursor: 'default' }}>{children}</span>
                    }

                    // nim-chip: numbered badge in the sources section footer
                    if (className === 'nim-chip') {
                      const dataProps = props as Record<string, unknown>
                      const sourceIndex = parseInt(String(dataProps['data-source-index'] ?? children), 10)
                      if (!isNaN(sourceIndex) && sources && onCitationClick) {
                        // source_index is unique per citation — find exact match
                        const source = sources.find((s) => s.index === sourceIndex)
                        const citation = citations?.find((c) => c.source_index === sourceIndex)
                        const tooltip = [source?.title, citation?.cited_text].filter(Boolean).join('\n\n')
                        if (source?.viewable) {
                          return (
                            <span
                              className="nim-chip"
                              style={{ cursor: 'pointer' }}
                              onClick={() => onCitationClick(source, citation?.cited_text ?? '', citation?.position_fraction ?? null)}
                              title={tooltip}
                            >
                              {children}
                            </span>
                          )
                        }
                        if (tooltip) {
                          return <span className="nim-chip" title={tooltip}>{children}</span>
                        }
                      }
                      return <span className="nim-chip">{children}</span>
                    }

                    // nim-sources-label: render singular/plural via i18n
                    if (className === 'nim-sources-label') {
                      const count = message.sources?.length ?? 0
                      return (
                        <span className="nim-sources-label">
                          {count === 1 ? t('chat.source') : t('chat.sources')}
                        </span>
                      )
                    }

                    return <span className={className} {...props}>{children}</span>
                  },
                }}
              >
                {message.content}
              </ReactMarkdown>
            </>
          )}
        </div>

        {/* Message Actions — hidden while streaming */}
        {!message.isStreaming && <><div className="flex items-center gap-2 mt-2">
          {canSubmitFeedback && (
            <>
              <button
                onClick={handleThumbsUp}
                disabled={feedbackSubmitted && feedbackType !== 'positive'}
                className={`p-1.5 rounded transition-colors ${feedbackType === 'positive'
                  ? 'bg-green-50 border border-green-500'
                  : 'hover:bg-gray-100'
                  } ${feedbackSubmitted ? 'opacity-60 cursor-default' : ''}`}
                title={t('chat.goodResponse')}
              >
                <ThumbsUp
                  className={`w-4 h-4 ${feedbackType === 'positive' ? 'text-green-600' : 'text-gray-400'
                    }`}
                />
              </button>
              <button
                onClick={handleThumbsDown}
                disabled={feedbackSubmitted && feedbackType !== 'negative'}
                className={`p-1.5 rounded transition-colors ${feedbackType === 'negative'
                  ? 'bg-red-50 border border-red-500'
                  : 'hover:bg-gray-100'
                  } ${feedbackSubmitted ? 'opacity-60 cursor-default' : ''}`}
                title={t('chat.badResponse')}
              >
                <ThumbsDown
                  className={`w-4 h-4 ${feedbackType === 'negative' ? 'text-red-600' : 'text-gray-400'
                    }`}
                />
              </button>
            </>
          )}
          <span className="text-[10px] text-gray-400">{formatTime(message.timestamp)}</span>
        </div>

          {/* Feedback Input */}
          {feedbackType && !feedbackSubmitted && (
            <div
              className={`mt-3 p-3 rounded-lg border ${feedbackType === 'positive'
                ? 'border-green-300 bg-green-50'
                : 'border-red-300 bg-red-50'
                }`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="text"
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={t('chat.feedbackPlaceholder')}
                  className={`flex-1 px-3 py-2 text-sm border rounded-md outline-none ${feedbackType === 'positive'
                    ? 'border-green-300 bg-white focus:ring-1 focus:ring-green-400'
                    : 'border-red-300 bg-white focus:ring-1 focus:ring-red-400'
                    }`}
                />
                <button
                  onClick={handleSubmitFeedback}
                  disabled={feedbackMutation.isPending}
                  className={`px-4 py-2 text-sm text-white rounded-md transition-colors ${feedbackType === 'positive'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                    } ${feedbackMutation.isPending ? 'opacity-50' : ''}`}
                >
                  {feedbackMutation.isPending ? t('chat.feedbackSending') : t('chat.feedbackSubmit')}
                </button>
              </div>
            </div>
          )}
        </>}
      </div>
    </div>
  )
}

